import { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';

/**
 * Ersetzt die nativen window.confirm/alert/prompt durch gestaltete Meldungen im
 * Projekt-Design (mittig). Promise-basiert:
 *   const { confirm, alert, prompt } = useDialog();
 *   if (await confirm({ message: '…' })) { … }
 */
interface BaseOpts {
  title?: string;
  message: string;
  okLabel?: string;
  cancelLabel?: string;
  danger?: boolean;        // roter Bestätigen-Button (destruktiv)
}
interface PromptOpts extends BaseOpts { placeholder?: string; defaultValue?: string; multiline?: boolean; }

type DialogState =
  | ({ kind: 'confirm' | 'alert'; resolve: (v: boolean) => void } & BaseOpts)
  | ({ kind: 'prompt'; resolve: (v: string | null) => void } & PromptOpts);

interface DialogApi {
  confirm: (o: BaseOpts) => Promise<boolean>;
  alert: (o: BaseOpts | string) => Promise<void>;
  prompt: (o: PromptOpts) => Promise<string | null>;
}

const Ctx = createContext<DialogApi | null>(null);

export function useDialog(): DialogApi {
  const v = useContext(Ctx);
  if (!v) throw new Error('useDialog muss innerhalb von <ConfirmProvider> genutzt werden');
  return v;
}

export function ConfirmProvider({ children }: { children: React.ReactNode }) {
  const [state, setState] = useState<DialogState | null>(null);
  const [input, setInput] = useState('');
  const inputRef = useRef<HTMLTextAreaElement | HTMLInputElement | null>(null);

  const confirm = useCallback((o: BaseOpts) => new Promise<boolean>((resolve) => {
    setState({ kind: 'confirm', resolve, ...o });
  }), []);
  const alert = useCallback((o: BaseOpts | string) => new Promise<void>((resolve) => {
    const opts = typeof o === 'string' ? { message: o } : o;
    setState({ kind: 'alert', resolve: () => resolve(), ...opts });
  }), []);
  const prompt = useCallback((o: PromptOpts) => new Promise<string | null>((resolve) => {
    setInput(o.defaultValue || '');
    setState({ kind: 'prompt', resolve, ...o });
  }), []);

  const close = (result: boolean | string | null) => {
    if (!state) return;
    if (state.kind === 'prompt') (state.resolve as (v: string | null) => void)(result === false ? null : String(result));
    else (state.resolve as (v: boolean) => void)(result === true);
    setState(null);
  };
  const onOk = () => close(state?.kind === 'prompt' ? input : true);
  const onCancel = () => close(state?.kind === 'prompt' ? false : false);

  useEffect(() => {
    if (state?.kind === 'prompt') setTimeout(() => inputRef.current?.focus(), 30);
  }, [state]);
  useEffect(() => {
    if (!state) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') { e.preventDefault(); onCancel(); }
      // Enter bestätigt — bei mehrzeiligem Prompt nur mit Ctrl/Cmd, sonst normale Zeilenumbrüche.
      else if (e.key === 'Enter' && (!(state.kind === 'prompt' && (state as PromptOpts).multiline) || e.metaKey || e.ctrlKey)) {
        e.preventDefault(); onOk();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state, input]);

  return (
    <Ctx.Provider value={{ confirm, alert, prompt }}>
      {children}
      {state && (
        <div className="fixed inset-0 z-[200] flex items-center justify-center bg-black/50 p-4">
          <div className="w-full max-w-md rounded-lg bg-white p-5 shadow-2xl">
            {state.title && <h3 className="text-base font-semibold text-ps-dark-800">{state.title}</h3>}
            <p className={`whitespace-pre-line text-sm text-ps-dark-600 ${state.title ? 'mt-1.5' : ''}`}>{state.message}</p>
            {state.kind === 'prompt' && (
              (state as PromptOpts).multiline ? (
                <textarea ref={(el) => { inputRef.current = el; }} value={input} onChange={(e) => setInput(e.target.value)}
                  placeholder={(state as PromptOpts).placeholder} rows={3}
                  className="mt-3 w-full resize-y rounded border border-ps-dark-200 px-2 py-1.5 text-sm focus:border-ps-orange-300 focus:outline-none" />
              ) : (
                <input ref={(el) => { inputRef.current = el; }} value={input} onChange={(e) => setInput(e.target.value)}
                  placeholder={(state as PromptOpts).placeholder}
                  className="mt-3 w-full rounded border border-ps-dark-200 px-2 py-1.5 text-sm focus:border-ps-orange-300 focus:outline-none" />
              )
            )}
            <div className="mt-4 flex justify-end gap-2">
              {state.kind !== 'alert' && (
                <button onClick={onCancel}
                  className="rounded-md border border-ps-dark-200 px-4 py-1.5 text-sm text-ps-dark-600 hover:bg-ps-dark-50">
                  {state.cancelLabel || 'Abbrechen'}
                </button>
              )}
              <button onClick={onOk} autoFocus={state.kind !== 'prompt'}
                className={`rounded-md px-4 py-1.5 text-sm font-medium text-white ${
                  state.danger ? 'bg-rose-600 hover:bg-rose-700' : 'bg-ps-orange-600 hover:bg-ps-orange-700'}`}>
                {state.okLabel || 'OK'}
              </button>
            </div>
          </div>
        </div>
      )}
    </Ctx.Provider>
  );
}
